"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import {
  ChatMode,
  type ChatState,
  type NavigationState,
} from "@/types/frontend-types";
import { useApi } from "@/contexts/api";
import { LogLevel, useSeqLog } from "@/hooks/telemetry";
import {
  buildProductStackFromProductId,
  buildProductStackFromUrl,
} from "@/utils/productTree";
import type { NavigateToProductOptions, SessionCollab } from "./context";

enum ViewingUrlDraftKind {
  Clear = "clear",
  Set = "set",
  MissingProduct = "missingProduct",
}

type HistoryWriteMode =
  typeof HISTORY_WRITE_MODE_PUSH | typeof HISTORY_WRITE_MODE_REPLACE;

type ViewingUrlDraft =
  | { kind: ViewingUrlDraftKind.Clear; url: string }
  | { kind: ViewingUrlDraftKind.Set; url: string; shortId: string }
  | { kind: ViewingUrlDraftKind.MissingProduct };
type WritableViewingUrlDraft = Exclude<
  ViewingUrlDraft,
  { kind: ViewingUrlDraftKind.MissingProduct }
>;

type ApiClient = ReturnType<typeof useApi>;
type SessionDataLog = ReturnType<typeof useSeqLog>;

interface UseSessionNavigationInput {
  api: ApiClient;
  sessionId: string;
  products: Record<string, ProductSummary>;
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  rootProductIdRef: MutableRefObject<string | null>;
  urlRootProductId: string | null;
  collabsByProduct: Record<string, string[]>;
  chatPageLimit: number;
  refClickLog: SessionDataLog;
  setChat: Dispatch<SetStateAction<ChatState>>;
  replaceProductCollabItems: (input: {
    productId: string;
    items: readonly SessionCollab[];
  }) => void;
}

interface RestoreInitialNavigationInput {
  productsMap: Record<string, ProductSummary>;
  rootProductId: string | null;
}

type NavigationSetter = Dispatch<SetStateAction<NavigationState>>;
type PushViewingUrl = (
  productId: string | null,
  options?: NavigateToProductOptions,
) => void;

interface InitialRootNavigationInput {
  rootProductId: string;
  refClickLog: SessionDataLog;
  setNavigation: NavigationSetter;
  caller: string;
  message: string;
}

interface PopstateNavigationResult {
  viewing: string | null;
  nextFocusedProductId: string;
  nextProductStack: string[];
}

interface HistoryBackActions {
  goBack: () => void;
  goBackN: (count: number) => number;
}

interface NavigationFocusLogInput {
  focusedProductId: string | null;
  refClickLog: SessionDataLog;
}

interface NavigationRefInput {
  navigation: NavigationState;
}

interface InitialNavigationRestorerInput {
  refClickLog: SessionDataLog;
  setNavigation: NavigationSetter;
}

interface ResolvedInitialViewingInput {
  productsMap: Record<string, ProductSummary>;
  rootProductId: string;
  focusedProductId: string;
  productStack: string[];
  refClickLog: SessionDataLog;
  setNavigation: NavigationSetter;
}

interface ViewingUrlWriterInput {
  products: Record<string, ProductSummary>;
  refClickLog: SessionDataLog;
  urlRootProductId: string | null;
}

interface PopstateNavigationInput {
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  refClickLog: SessionDataLog;
  rootProductIdRef: MutableRefObject<string | null>;
  setChat: Dispatch<SetStateAction<ChatState>>;
  setNavigation: NavigationSetter;
}

interface FocusProductActionInput {
  api: ApiClient;
  chatPageLimit: number;
  collabsByProduct: Record<string, string[]>;
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  pushViewingUrl: PushViewingUrl;
  refClickLog: SessionDataLog;
  replaceProductCollabItems: (input: {
    productId: string;
    items: readonly SessionCollab[];
  }) => void;
  rootProductIdRef: MutableRefObject<string | null>;
  sessionId: string;
  setChat: Dispatch<SetStateAction<ChatState>>;
  setNavigation: NavigationSetter;
}

interface RunFocusProductActionInput extends FocusProductActionInput {
  productId: string;
}

interface MissingProductCollabsInput {
  api: ApiClient;
  chatPageLimit: number;
  collabsByProduct: Record<string, string[]>;
  productId: string;
  replaceProductCollabItems: (input: {
    productId: string;
    items: readonly SessionCollab[];
  }) => void;
  sessionId: string;
}

interface NavigateToProductActionInput {
  products: Record<string, ProductSummary>;
  pushViewingUrl: PushViewingUrl;
  refClickLog: SessionDataLog;
  rootProductIdRef: MutableRefObject<string | null>;
  setChat: Dispatch<SetStateAction<ChatState>>;
  setNavigation: NavigationSetter;
}

interface HistoryBackActionsInput {
  navigationRef: MutableRefObject<NavigationState>;
  refClickLog: SessionDataLog;
}

interface SessionNavigationActionsHookInput {
  navigationRef: MutableRefObject<NavigationState>;
  pushViewingUrl: PushViewingUrl;
  sessionInput: UseSessionNavigationInput;
  setNavigation: NavigationSetter;
}

export interface SessionNavigationActions {
  focusProduct: (productId: string) => Promise<void>;
  navigateToProduct: (
    productId: string,
    options?: NavigateToProductOptions,
  ) => void;
  goBack: () => void;
  goBackN: (count: number) => number;
  selectVersion: (versionId: string | null) => void;
}

export interface SessionNavigationState extends SessionNavigationActions {
  navigation: NavigationState;
  restoreInitialNavigation: (input: RestoreInitialNavigationInput) => void;
}

const CALLER_INITIAL_FETCH_VIEWING_VALID = "initial-fetch-viewing-valid";
const CALLER_INITIAL_FETCH_VIEWING_INVALID = "initial-fetch-viewing-invalid";
const CALLER_INITIAL_FETCH_NO_VIEWING = "initial-fetch-no-viewing";
const CALLER_POPSTATE = "popstate";
const CALLER_FOCUS_PRODUCT = "focusProduct";
const CALLER_NAVIGATE_TO_PRODUCT = "navigateToProduct";
const HISTORY_WRITE_MODE_PUSH = "push";
const HISTORY_WRITE_MODE_REPLACE = "replace";

export function useSessionNavigation(
  sessionInput: UseSessionNavigationInput,
): SessionNavigationState {
  const [navigation, setNavigation] = useState<NavigationState>({
    focusedProductId: null,
    productStack: [],
    selectedVersionId: null,
  });

  useNavigationFocusLog({
    focusedProductId: navigation.focusedProductId,
    refClickLog: sessionInput.refClickLog,
  });

  const navigationRef = useNavigationRef({ navigation });
  const restoreInitialNavigation = useInitialNavigationRestorer({
    refClickLog: sessionInput.refClickLog,
    setNavigation,
  });
  const pushViewingUrl = useViewingUrlWriter({
    products: sessionInput.products,
    refClickLog: sessionInput.refClickLog,
    urlRootProductId: sessionInput.urlRootProductId,
  });

  usePopstateNavigation({
    productsRef: sessionInput.productsRef,
    refClickLog: sessionInput.refClickLog,
    rootProductIdRef: sessionInput.rootProductIdRef,
    setChat: sessionInput.setChat,
    setNavigation,
  });

  const actions = useSessionNavigationActions({
    navigationRef,
    pushViewingUrl,
    sessionInput,
    setNavigation,
  });

  return {
    navigation,
    restoreInitialNavigation,
    ...actions,
  };
}

function useSessionNavigationActions({
  navigationRef,
  pushViewingUrl,
  sessionInput,
  setNavigation,
}: SessionNavigationActionsHookInput): SessionNavigationActions {
  const focusProduct = useFocusProductAction({
    api: sessionInput.api,
    chatPageLimit: sessionInput.chatPageLimit,
    collabsByProduct: sessionInput.collabsByProduct,
    productsRef: sessionInput.productsRef,
    pushViewingUrl,
    refClickLog: sessionInput.refClickLog,
    replaceProductCollabItems: sessionInput.replaceProductCollabItems,
    rootProductIdRef: sessionInput.rootProductIdRef,
    sessionId: sessionInput.sessionId,
    setChat: sessionInput.setChat,
    setNavigation,
  });
  const { goBack, goBackN } = useHistoryBackActions({
    navigationRef,
    refClickLog: sessionInput.refClickLog,
  });
  const selectVersion = useSelectVersionAction({ setNavigation });
  const navigateToProduct = useNavigateToProductAction({
    products: sessionInput.products,
    pushViewingUrl,
    refClickLog: sessionInput.refClickLog,
    rootProductIdRef: sessionInput.rootProductIdRef,
    setChat: sessionInput.setChat,
    setNavigation,
  });

  return {
    focusProduct,
    navigateToProduct,
    goBack,
    goBackN,
    selectVersion,
  };
}

function useNavigationFocusLog({
  focusedProductId,
  refClickLog,
}: NavigationFocusLogInput): void {
  useEffect(() => {
    refClickLog(
      "NAV state navigation.focusedProductId observed",
      {
        focusedProductId,
        url: getBrowserUrlForLog(),
      },
      LogLevel.Info,
    );
  }, [focusedProductId, refClickLog]);
}

function useNavigationRef({
  navigation,
}: NavigationRefInput): MutableRefObject<NavigationState> {
  const navigationRef = useRef(navigation);
  useLayoutEffect(() => {
    navigationRef.current = navigation;
  }, [navigation]);
  return navigationRef;
}

function useInitialNavigationRestorer({
  refClickLog,
  setNavigation,
}: InitialNavigationRestorerInput): (
  input: RestoreInitialNavigationInput,
) => void {
  return useCallback(
    ({ productsMap, rootProductId }): void => {
      if (!rootProductId) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const viewingParam = urlParams.get("viewing");
      if (!viewingParam) {
        setInitialRootNavigation({
          rootProductId,
          refClickLog,
          setNavigation,
          caller: CALLER_INITIAL_FETCH_NO_VIEWING,
          message: "INIT-3 initial fetch: setNavigation (no viewing -> root)",
        });
        return;
      }

      const stackResult = buildProductStackFromUrl(
        productsMap,
        viewingParam,
        rootProductId,
      );
      if (!stackResult.focusedProductId) {
        restoreInvalidInitialViewing({
          rootProductId,
          refClickLog,
          setNavigation,
          urlParams,
        });
        return;
      }

      restoreValidInitialViewing({
        productsMap,
        rootProductId,
        focusedProductId: stackResult.focusedProductId,
        productStack: stackResult.productStack,
        refClickLog,
        setNavigation,
      });
    },
    [refClickLog, setNavigation],
  );
}

function setInitialRootNavigation({
  rootProductId,
  refClickLog,
  setNavigation,
  caller,
  message,
}: InitialRootNavigationInput): void {
  setNavigation((previous) => {
    refClickLog(
      message,
      {
        from: previous.focusedProductId,
        to: rootProductId,
        caller,
      },
      LogLevel.Warning,
    );
    return {
      ...previous,
      focusedProductId: rootProductId,
    };
  });
}

function restoreInvalidInitialViewing(input: {
  rootProductId: string;
  refClickLog: SessionDataLog;
  setNavigation: NavigationSetter;
  urlParams: URLSearchParams;
}): void {
  input.urlParams.delete("viewing");
  const newUrl = buildUrlFromSearchParams(input.urlParams);
  window.history.replaceState(window.history.state, "", newUrl);
  setInitialRootNavigation({
    rootProductId: input.rootProductId,
    refClickLog: input.refClickLog,
    setNavigation: input.setNavigation,
    caller: CALLER_INITIAL_FETCH_VIEWING_INVALID,
    message:
      "INIT-2 initial fetch: setNavigation (viewing param invalid -> root)",
  });
}

function restoreValidInitialViewing({
  productsMap,
  rootProductId,
  focusedProductId,
  productStack,
  refClickLog,
  setNavigation,
}: ResolvedInitialViewingInput): void {
  setNavigation((previous) => {
    refClickLog(
      "INIT-1 initial fetch: setNavigation (viewing param valid)",
      {
        from: previous.focusedProductId,
        to: focusedProductId,
        caller: CALLER_INITIAL_FETCH_VIEWING_VALID,
      },
      LogLevel.Warning,
    );
    return {
      ...previous,
      focusedProductId,
      productStack,
    };
  });
  rebuildInitialViewingHistory({
    productsMap,
    rootProductId,
    focusedProductId,
    productStack,
  });
}

function rebuildInitialViewingHistory(input: {
  productsMap: Record<string, ProductSummary>;
  rootProductId: string;
  focusedProductId: string;
  productStack: string[];
}): void {
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname,
  );
  if (input.focusedProductId === input.rootProductId) {
    return;
  }

  const levelsAboveRoot = [
    ...input.productStack.slice(1),
    input.focusedProductId,
  ];
  levelsAboveRoot.forEach((productId) => {
    const shortId = input.productsMap[productId]?.shortId.canonical;
    if (!shortId) return;
    window.history.pushState(
      window.history.state,
      "",
      `${window.location.pathname}?viewing=${shortId}`,
    );
  });
}

function useViewingUrlWriter({
  products,
  refClickLog,
  urlRootProductId,
}: ViewingUrlWriterInput): PushViewingUrl {
  return useCallback(
    (productId: string | null, options?: NavigateToProductOptions): void => {
      const historyMode = selectHistoryWriteMode({ options });
      logPushViewingUrlEntry({
        productId,
        urlRootProductId,
        products,
        historyMode,
        refClickLog,
      });
      const draft = selectViewingUrlDraft({
        productId,
        rootProductId: urlRootProductId,
        products,
      });
      const writableDraft = selectWritableViewingUrlDraft({
        draft,
        productId,
        urlRootProductId,
        refClickLog,
      });
      if (!writableDraft) {
        return;
      }

      if (
        shouldSkipViewingUrlWrite({
          newUrl: writableDraft.url,
          historyMode,
          refClickLog,
        })
      ) {
        return;
      }

      commitViewingUrlDraft({
        url: writableDraft.url,
        historyMode,
        refClickLog,
      });
    },
    [products, refClickLog, urlRootProductId],
  );
}

function usePopstateNavigation(input: PopstateNavigationInput): void {
  const { productsRef, refClickLog, rootProductIdRef, setChat, setNavigation } =
    input;
  useEffect(() => {
    const handler = createPopstateHandler({
      productsRef,
      refClickLog,
      rootProductIdRef,
      setChat,
      setNavigation,
    });
    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("popstate", handler);
    };
  }, [productsRef, refClickLog, rootProductIdRef, setChat, setNavigation]);
}

function createPopstateHandler(input: PopstateNavigationInput): () => void {
  return (): void => {
    const root = input.rootProductIdRef.current;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    input.refClickLog(
      "POP-1 SessionDataProvider popstate handler fired",
      {
        root,
        url: currentUrl,
      },
      LogLevel.Warning,
    );
    if (!root) {
      input.refClickLog(
        "POP-2 popstate: no root, early return",
        {},
        LogLevel.Info,
      );
      return;
    }

    const nextNavigation = selectPopstateNavigation({
      products: input.productsRef.current,
      root,
    });
    logPopstateNavigationResult({
      nextNavigation,
      refClickLog: input.refClickLog,
    });
    input.setNavigation((previous) =>
      reconcilePopstateNavigation({
        previous,
        nextNavigation,
        refClickLog: input.refClickLog,
        setChat: input.setChat,
      }),
    );
  };
}

function selectPopstateNavigation(input: {
  products: Record<string, ProductSummary>;
  root: string;
}): PopstateNavigationResult {
  const viewing = new URLSearchParams(window.location.search).get("viewing");
  let nextFocusedProductId: string = input.root;
  let nextProductStack: string[] = [];
  if (viewing) {
    const result = buildProductStackFromUrl(
      input.products,
      viewing,
      input.root,
    );
    if (result.focusedProductId) {
      nextFocusedProductId = result.focusedProductId;
      nextProductStack = result.productStack;
    }
  }
  return { viewing, nextFocusedProductId, nextProductStack };
}

function logPopstateNavigationResult(input: {
  nextNavigation: PopstateNavigationResult;
  refClickLog: SessionDataLog;
}): void {
  input.refClickLog(
    "POP-3 popstate: computed next focusedProductId from URL",
    {
      viewing: input.nextNavigation.viewing,
      nextFocusedProductId: input.nextNavigation.nextFocusedProductId,
      nextProductStackLength: input.nextNavigation.nextProductStack.length,
    },
    LogLevel.Warning,
  );
}

function reconcilePopstateNavigation(input: {
  previous: NavigationState;
  nextNavigation: PopstateNavigationResult;
  refClickLog: SessionDataLog;
  setChat: Dispatch<SetStateAction<ChatState>>;
}): NavigationState {
  const sameStack = areProductStacksEqual({
    currentStack: input.previous.productStack,
    nextStack: input.nextNavigation.nextProductStack,
  });
  if (
    input.previous.focusedProductId ===
      input.nextNavigation.nextFocusedProductId &&
    sameStack
  ) {
    input.refClickLog(
      "POP-4 popstate: no change, returning previous",
      {
        focusedProductId: input.previous.focusedProductId,
      },
      LogLevel.Info,
    );
    return input.previous;
  }

  if (
    input.previous.focusedProductId !==
    input.nextNavigation.nextFocusedProductId
  ) {
    input.refClickLog(
      "POP-5 popstate: setNavigation overwriting focus + main chat",
      {
        from: input.previous.focusedProductId,
        to: input.nextNavigation.nextFocusedProductId,
        caller: CALLER_POPSTATE,
      },
      LogLevel.Error,
    );
    input.setChat(createMainChatState());
  }
  return {
    ...input.previous,
    focusedProductId: input.nextNavigation.nextFocusedProductId,
    productStack: input.nextNavigation.nextProductStack,
  };
}

function useFocusProductAction(
  input: FocusProductActionInput,
): (productId: string) => Promise<void> {
  const {
    api,
    chatPageLimit,
    collabsByProduct,
    productsRef,
    pushViewingUrl,
    refClickLog,
    replaceProductCollabItems,
    rootProductIdRef,
    sessionId,
    setChat,
    setNavigation,
  } = input;
  return useCallback(
    async (productId: string): Promise<void> => {
      await runFocusProductAction({
        api,
        chatPageLimit,
        collabsByProduct,
        productId,
        productsRef,
        pushViewingUrl,
        refClickLog,
        replaceProductCollabItems,
        rootProductIdRef,
        sessionId,
        setChat,
        setNavigation,
      });
    },
    [
      api,
      chatPageLimit,
      collabsByProduct,
      productsRef,
      pushViewingUrl,
      refClickLog,
      replaceProductCollabItems,
      rootProductIdRef,
      sessionId,
      setChat,
      setNavigation,
    ],
  );
}

async function runFocusProductAction({
  api,
  chatPageLimit,
  collabsByProduct,
  productId,
  productsRef,
  pushViewingUrl,
  refClickLog,
  replaceProductCollabItems,
  rootProductIdRef,
  sessionId,
  setChat,
  setNavigation,
}: RunFocusProductActionInput): Promise<void> {
  refClickLog("FP-1 focusProduct entry", { productId }, LogLevel.Info);
  const rootProductId = rootProductIdRef.current;
  const stackResult = buildProductStackFromProductId(
    productsRef.current,
    productId,
    rootProductId,
  );
  if (!stackResult.focusedProductId) {
    refClickLog(
      "FP-X focusProduct: unresolved product/chain - no-op",
      {
        productId,
      },
      LogLevel.Warning,
    );
    return;
  }

  applyFocusedProductNavigation({
    productId,
    rootProductId,
    productStack: stackResult.productStack,
    refClickLog,
    setNavigation,
  });
  logFocusProductUrlPush({ productId, pushViewingUrl, refClickLog });
  setChat(createMainChatState());
  await loadMissingProductCollabs({
    api,
    chatPageLimit,
    collabsByProduct,
    productId,
    replaceProductCollabItems,
    sessionId,
  });
}

function applyFocusedProductNavigation(input: {
  productId: string;
  rootProductId: string | null;
  productStack: string[];
  refClickLog: SessionDataLog;
  setNavigation: NavigationSetter;
}): void {
  input.setNavigation((previous) => {
    input.refClickLog(
      "FP-2 focusProduct: setNavigation",
      {
        from: previous.focusedProductId,
        to: input.productId,
        caller: CALLER_FOCUS_PRODUCT,
        rootProductId: input.rootProductId,
        targetIsRoot: input.productId === input.rootProductId,
        prevProductStack: previous.productStack,
        nextProductStack: input.productStack,
        nextFocusedProductId: input.productId,
        resultingDisplayStack: [...input.productStack, input.productId],
        targetAlreadyInNextStack: input.productStack.includes(input.productId),
      },
      LogLevel.Warning,
    );
    return {
      ...previous,
      focusedProductId: input.productId,
      productStack: input.productStack,
    };
  });
}

function logFocusProductUrlPush(input: {
  productId: string;
  pushViewingUrl: PushViewingUrl;
  refClickLog: SessionDataLog;
}): void {
  input.refClickLog(
    "FP-3 focusProduct: calling pushViewingUrl",
    {
      productId: input.productId,
    },
    LogLevel.Info,
  );
  input.pushViewingUrl(input.productId);
  input.refClickLog(
    "FP-4 focusProduct: pushViewingUrl returned",
    {
      productId: input.productId,
    },
    LogLevel.Info,
  );
}

async function loadMissingProductCollabs({
  api,
  chatPageLimit,
  collabsByProduct,
  productId,
  replaceProductCollabItems,
  sessionId,
}: MissingProductCollabsInput): Promise<void> {
  if (collabsByProduct[productId]) {
    return;
  }

  try {
    const response = await api.sessions.byId(sessionId).collabs({
      query: {
        productId,
        limit: chatPageLimit,
      },
    });
    const items = response.items.map((collab) => toSessionCollab({ collab }));
    replaceProductCollabItems({
      productId,
      items,
    });
  } catch {}
}

function useHistoryBackActions({
  navigationRef,
  refClickLog,
}: HistoryBackActionsInput): HistoryBackActions {
  const goBack = useCallback((): void => {
    if (navigationRef.current.productStack.length === 0) return;
    refClickLog(
      "GB goBack: calling window.history.back()",
      {
        stackLength: navigationRef.current.productStack.length,
      },
      LogLevel.Warning,
    );
    window.history.back();
  }, [navigationRef, refClickLog]);

  const goBackN = useCallback(
    (count: number): number => {
      const normalized = Math.max(0, Math.floor(count));
      const currentStack = navigationRef.current.productStack;
      const actualPops = Math.min(normalized, currentStack.length);
      if (actualPops === 0) return 0;
      refClickLog(
        "GBN goBackN: calling window.history.go",
        {
          delta: -actualPops,
        },
        LogLevel.Warning,
      );
      window.history.go(-actualPops);
      return actualPops;
    },
    [navigationRef, refClickLog],
  );

  return { goBack, goBackN };
}

function useSelectVersionAction(input: {
  setNavigation: NavigationSetter;
}): (versionId: string | null) => void {
  const { setNavigation } = input;
  return useCallback(
    (versionId: string | null): void => {
      setNavigation((previous) => ({
        ...previous,
        selectedVersionId: versionId,
      }));
    },
    [setNavigation],
  );
}

function useNavigateToProductAction(
  input: NavigateToProductActionInput,
): (productId: string, options?: NavigateToProductOptions) => void {
  const {
    products,
    pushViewingUrl,
    refClickLog,
    rootProductIdRef,
    setChat,
    setNavigation,
  } = input;
  return useCallback(
    (productId: string, options?: NavigateToProductOptions): void => {
      const rootProductId = rootProductIdRef.current;
      if (!rootProductId) return;

      const stackResult = buildProductStackFromProductId(
        products,
        productId,
        rootProductId,
      );
      if (!stackResult.focusedProductId) {
        return;
      }

      setNavigation((previous) => {
        refClickLog(
          "NTP navigateToProduct: setNavigation",
          {
            from: previous.focusedProductId,
            to: stackResult.focusedProductId,
            caller: CALLER_NAVIGATE_TO_PRODUCT,
            replace: options?.replace ?? false,
          },
          LogLevel.Warning,
        );
        return {
          ...previous,
          focusedProductId: stackResult.focusedProductId,
          productStack: stackResult.productStack,
        };
      });

      pushViewingUrl(productId, options);
      setChat(createMainChatState());
    },
    [
      products,
      pushViewingUrl,
      refClickLog,
      rootProductIdRef,
      setChat,
      setNavigation,
    ],
  );
}

function createMainChatState(): ChatState {
  return {
    mode: ChatMode.Main,
    selectedProductId: null,
    targetVersionId: null,
    targetMessageId: null,
    targetRequestId: 0,
    indicatedMessageId: null,
    indicationRequestId: 0,
  };
}

function getBrowserUrlForLog(): string | null {
  return typeof window !== "undefined"
    ? `${window.location.pathname}${window.location.search}`
    : null;
}

function toSessionCollab(input: { collab: SessionCollab }): SessionCollab {
  return input.collab;
}

function areProductStacksEqual(input: {
  currentStack: readonly string[];
  nextStack: readonly string[];
}): boolean {
  if (input.currentStack.length !== input.nextStack.length) {
    return false;
  }

  return input.currentStack.every(
    (productId, index) => productId === input.nextStack[index],
  );
}

function selectHistoryWriteMode(input: {
  options?: NavigateToProductOptions;
}): HistoryWriteMode {
  return input.options?.replace
    ? HISTORY_WRITE_MODE_REPLACE
    : HISTORY_WRITE_MODE_PUSH;
}

function logPushViewingUrlEntry(input: {
  productId: string | null;
  urlRootProductId: string | null;
  products: Record<string, ProductSummary>;
  historyMode: HistoryWriteMode;
  refClickLog: SessionDataLog;
}): void {
  input.refClickLog(
    "PV-1 pushViewingUrl entry",
    {
      productId: input.productId,
      urlRootProductId: input.urlRootProductId,
      startUrl: getCurrentBrowserUrl(),
      hasProductInMap: input.productId
        ? Boolean(input.products[input.productId])
        : null,
      historyMode: input.historyMode,
    },
    LogLevel.Info,
  );
}

function selectWritableViewingUrlDraft(input: {
  draft: ViewingUrlDraft;
  productId: string | null;
  urlRootProductId: string | null;
  refClickLog: SessionDataLog;
}): WritableViewingUrlDraft | null {
  if (input.draft.kind === ViewingUrlDraftKind.Clear) {
    input.refClickLog(
      "PV-2 pushViewingUrl: branch=clear-viewing",
      {
        productId: input.productId,
        urlRootProductId: input.urlRootProductId,
      },
      LogLevel.Info,
    );
    return input.draft;
  }

  if (input.draft.kind === ViewingUrlDraftKind.MissingProduct) {
    input.refClickLog(
      "PV-3 pushViewingUrl: EARLY RETURN - product not in map",
      {
        productId: input.productId,
      },
      LogLevel.Warning,
    );
    return null;
  }

  input.refClickLog(
    "PV-4 pushViewingUrl: branch=set-viewing",
    {
      productId: input.productId,
      shortId: input.draft.shortId,
    },
    LogLevel.Info,
  );
  return input.draft;
}

function shouldSkipViewingUrlWrite(input: {
  newUrl: string;
  historyMode: HistoryWriteMode;
  refClickLog: SessionDataLog;
}): boolean {
  const currentUrl = getCurrentBrowserUrl();
  if (input.newUrl !== currentUrl) {
    return false;
  }

  input.refClickLog(
    "PV-5 pushViewingUrl: URL unchanged, skipping history write",
    {
      currentUrl,
      historyMode: input.historyMode,
    },
    LogLevel.Info,
  );
  return true;
}

function commitViewingUrlDraft(input: {
  url: string;
  historyMode: HistoryWriteMode;
  refClickLog: SessionDataLog;
}): void {
  const currentUrl = getCurrentBrowserUrl();
  input.refClickLog(
    "PV-6 pushViewingUrl: writing browser URL",
    {
      from: currentUrl,
      to: input.url,
      historyMode: input.historyMode,
    },
    LogLevel.Warning,
  );
  writeViewingUrl({ historyMode: input.historyMode, url: input.url });
  input.refClickLog(
    "PV-7 pushViewingUrl: browser URL write complete",
    {
      to: input.url,
      historyMode: input.historyMode,
    },
    LogLevel.Info,
  );
}

function selectViewingUrlDraft(input: {
  productId: string | null;
  rootProductId: string | null;
  products: Record<string, ProductSummary>;
}): ViewingUrlDraft {
  const params = new URLSearchParams(window.location.search);

  if (!input.productId || input.productId === input.rootProductId) {
    params.delete("viewing");
    return {
      kind: ViewingUrlDraftKind.Clear,
      url: buildUrlFromSearchParams(params),
    };
  }

  const product = input.products[input.productId];
  if (!product) {
    return { kind: ViewingUrlDraftKind.MissingProduct };
  }

  params.set("viewing", product.shortId.canonical);
  return {
    kind: ViewingUrlDraftKind.Set,
    url: buildUrlFromSearchParams(params),
    shortId: product.shortId.canonical,
  };
}

function getCurrentBrowserUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function buildUrlFromSearchParams(params: URLSearchParams): string {
  const queryString = params.toString();
  return queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
}

function writeViewingUrl(input: {
  historyMode: HistoryWriteMode;
  url: string;
}): void {
  if (input.historyMode === HISTORY_WRITE_MODE_REPLACE) {
    window.history.replaceState(window.history.state, "", input.url);
    return;
  }

  window.history.pushState(window.history.state, "", input.url);
}
